// R11-2 — THE PETITION PAPER, WHICH IS THE ONE DELIVERED THING ON THIS SITE THAT IS NOT A REPORT.
//
// Eleventh shell out of App.tsx, moved so `ReportBody` — the dispatcher that names it — could follow.
// It takes props, holds no state and calls no hook, so scripts/r11-dispatch.mjs CALLS it, and what
// that buys is W12-3's ruling held as arithmetic instead of as a source regex: **the target's name is
// written PLAIN.** It used to be drawn with a red rule struck through it, twice (a rotated CSS
// ::after and a stroked line in the PDF), and her crossing is not that — it is the command written BY
// HAND across the name, which is step 2 of the instructions underneath. A struck name printed on a
// delivered petition reads as a name someone deleted.
//
// Moved VERBATIM. `reportSectionCard` was already out (R9-5) and `NarrativeSection` (R9-1).

import { reportSectionCard } from "./ReportView";
import type { NarrativeSection } from "./NarrativeReportView";
import menuLogoUrl from "../assets/menu-logo.webp";

// MR-P3 — a petition paper: the words + her Cross Method layout, rendered AS a paper.
export type PetitionPaperData = {
  type: "petition";
  header: { name: string; dateText?: string; place?: string };
  kicker?: string;
  divineName?: string;
  overview: string;
  paper: {
    invocation: string;
    method: string;
    targetName?: string;
    targetBirth?: string;
    command: string;
    caps?: boolean;
    closing: string;
    // W12-3(b): her closing formula, composed in code by server/petition-method.js for whichever god
    // the member chose. Optional only so a petition saved before W12-3 still opens.
    formula?: string;
  };
  sections: NarrativeSection[];
};

// MR-P3 — the petition PAPER on screen: a parchment panel carrying the invocation, the target's name
// crossed and covered by the command (her Cross Method) or the plain declaration, and the sealing line.
// The "how to work it" section follows beneath. Never a report/letter layout.
export function PetitionPaperView({ data }: { data: PetitionPaperData }) {
  const p = data.paper || ({} as PetitionPaperData["paper"]);
  const cross = String(p.method || "straight") !== "straight";
  const nameLine = [p.targetName, p.targetBirth].filter(Boolean).join(",  ");
  const command = p.caps ? String(p.command || "").toUpperCase() : String(p.command || "");
  const howTo = (data.sections || []).filter((s) => !/petition paper$/i.test(String(s.heading || "")));
  return (
    <article className="chart-report petition-report">
      <header className="chart-report__band">
        <img src={menuLogoUrl} alt="12th House" className="chart-report__logo" />
        <p className="chart-report__kicker">{data.kicker || "A Petition"}</p>
      </header>
      {data.overview ? <p className="petition-report__lede">{data.overview}</p> : null}
      <div className={`petition-paper${cross ? " petition-paper--cross" : ""}`} role="img" aria-label="Your petition paper">
        {p.invocation ? <p className="petition-paper__invocation">{p.invocation}</p> : null}
        {/* W12-3(a): the name is written PLAIN. It used to carry a red rule drawn across it, which
            is not her crossing — her crossing is done by hand, with the command written across the
            name in capitals on the member's own paper, and it is step 2 of the instructions below.
            A struck name printed on a delivered petition reads as a name someone deleted. */}
        {cross && nameLine ? <p className="petition-paper__name">{nameLine}</p> : null}
        <p className={`petition-paper__command${cross ? " is-crossed" : ""}`}>{command}</p>
        {p.closing ? <p className="petition-paper__closing">{p.closing}</p> : null}
        {/* W12-3(b): her closing formula, on every petition, in the name of the god she chose. */}
        {p.formula ? <p className="petition-paper__formula">{p.formula}</p> : null}
      </div>
      <section className="chart-report__reading">
        <div className="chart-report__cards">
          {howTo.map((s, i) => (
            reportSectionCard(i, s.heading || "", s.body)
          ))}
        </div>
      </section>
    </article>
  );
}
